import React, { useState } from 'react'
import { Box, CircularProgress, Alert, Button, FormControlLabel, Switch } from '@mui/material'
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getClassesDirect,
  createClassDirect,
  updateClassDirect,
  deleteClassDirect,
  reactivateClassDirect,
  getClassSectionsDirect,
  createClassSectionDirect,
  updateClassSectionDirect,
  deleteClassSectionDirect,
  getTeachersDirect,
  getClassSchedulesDirect,
  getClassGroupsDirect,
  createClassGroupDirect,
  updateClassGroupDirect,
  deleteClassGroupDirect,
} from '@timetable/api'
import type { Class, ClassSectionWithRelations } from '@timetable/types'
import { ClassList } from './ClassList'
import { ClassFormDialog } from './ClassFormDialog'
import { ClassGroupsDialog } from './ClassGroupsDialog'
import { SectionFormDialog } from './SectionFormDialog'
import { ConfirmDialog } from '../ConfirmDialog'
import { SearchBar, SortMenu } from '../shared'
import type { SortConfig } from '../shared'
import { useDebounce } from '../../hooks/useDebounce'
import { useToast } from '../Toast'
import { queryKeys } from '../../lib/queryKeys'

interface ClassesTabProps {
  schoolId: string
}

const SORT_OPTIONS = [
  { id: 'class', label: 'Class Name', field: 'class_name' },
  { id: 'class_group', label: 'Class Group', field: 'class_group' },
  { id: 'sections', label: 'Section Count', field: 'section_count' },
]

export const ClassesTab: React.FC<ClassesTabProps> = ({ schoolId }) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'class_name',
    direction: 'asc',
  })
  const [showInactive, setShowInactive] = useState(false)

  // Class dialog state
  const [classDialogOpen, setClassDialogOpen] = React.useState(false)
  const [selectedClass, setSelectedClass] = React.useState<Class | null>(null)
  const [classDeleteDialogOpen, setClassDeleteDialogOpen] = React.useState(false)
  const [classToDelete, setClassToDelete] = React.useState<Class | null>(null)
  const [classHasTimetableRecords, setClassHasTimetableRecords] = React.useState(false)

  // Class groups dialog state
  const [classGroupsDialogOpen, setClassGroupsDialogOpen] = React.useState(false)

  // Section dialog state
  const [sectionDialogOpen, setSectionDialogOpen] = React.useState(false)
  const [selectedSection, setSelectedSection] = React.useState<ClassSectionWithRelations | null>(null)
  const [selectedClassId, setSelectedClassId] = React.useState<string | null>(null)
  const [sectionDeleteDialogOpen, setSectionDeleteDialogOpen] = React.useState(false)
  const [sectionToDelete, setSectionToDelete] = React.useState<ClassSectionWithRelations | null>(null)

  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Fetch classes (with optional inactive)
  const {
    data: classes = [],
    isLoading: classesLoading,
    error: classesError,
  } = useQuery({
    queryKey: queryKeys.classes.list({ school_id: schoolId, includeInactive: showInactive }),
    queryFn: () => getClassesDirect(schoolId, { includeInactive: showInactive }),
    enabled: !!schoolId,
  })

  // Fetch class sections
  const {
    data: sections = [],
    isLoading: sectionsLoading,
    error: sectionsError,
  } = useQuery({
    queryKey: queryKeys.classSections.list({ school_id: schoolId }),
    queryFn: () => getClassSectionsDirect(schoolId),
    enabled: !!schoolId,
  })

  // Fetch teachers for class teacher assignment
  const { data: teachers = [] } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolId }),
    queryFn: () => getTeachersDirect({ school_id: schoolId }),
    enabled: !!schoolId,
  })

  // Fetch class schedules to display which schedule each class uses
  const { data: schedules = [] } = useQuery({
    queryKey: queryKeys.classSchedules.list({ school_id: schoolId }),
    queryFn: () => getClassSchedulesDirect(schoolId),
    enabled: !!schoolId,
  })

  // Fetch class groups for this school
  const { data: classGroups = [] } = useQuery({
    queryKey: queryKeys.classGroups.list(schoolId),
    queryFn: () => getClassGroupsDirect(schoolId),
    enabled: !!schoolId,
  })

  // Class group mutations
  const createClassGroupMutation = useMutation({
    mutationFn: (name: string) =>
      createClassGroupDirect({ school_id: schoolId, name, display_order: classGroups.length }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classGroups.all }),
    onError: (error: Error) => { throw error },
  })

  const updateClassGroupMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateClassGroupDirect(id, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classGroups.all }),
    onError: (error: Error) => { throw error },
  })

  const deleteClassGroupMutation = useMutation({
    mutationFn: deleteClassGroupDirect,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classGroups.all }),
    onError: (error: Error) => { throw error },
  })

  // Class mutations
  const createClassMutation = useMutation({
    mutationFn: async (data: { class_name: string; class_group: string; defaultSectionName?: string }) => {
      const newClass = await createClassDirect({
        school_id: schoolId,
        class_name: data.class_name,
        class_group: data.class_group,
      })

      if (data.defaultSectionName) {
        await createClassSectionDirect({
          class_id: newClass.id,
          section: data.defaultSectionName,
        })
      }

      return newClass
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classes.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all })
      setClassDialogOpen(false)
      setSelectedClass(null)
      showToast('Class created successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create class', 'error')
    },
  })

  const updateClassMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { class_name?: string; class_group?: string } }) =>
      updateClassDirect(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classes.all })
      setClassDialogOpen(false)
      setSelectedClass(null)
      showToast('Class updated successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update class', 'error')
    },
  })

  const deleteClassMutation = useMutation({
    mutationFn: deleteClassDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classes.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all })
      setClassDeleteDialogOpen(false)
      setClassToDelete(null)
      const msg = classHasTimetableRecords ? 'Class deactivated successfully' : 'Class deleted successfully'
      showToast(msg, 'success')
    },
    onError: (error: Error) => {
      showToast(
        error.message || 'Failed to delete class. It may have existing sections.',
        'error'
      )
    },
  })

  const reactivateClassMutation = useMutation({
    mutationFn: reactivateClassDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classes.all })
      showToast('Class reactivated successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to reactivate class', 'error')
    },
  })

  // Section mutations
  const createSectionMutation = useMutation({
    mutationFn: createClassSectionDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all })
      setSectionDialogOpen(false)
      setSelectedSection(null)
      setSelectedClassId(null)
      showToast('Section created successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create section', 'error')
    },
  })

  const updateSectionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { section?: string; class_teacher_id?: string } }) =>
      updateClassSectionDirect(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all })
      setSectionDialogOpen(false)
      setSelectedSection(null)
      showToast('Section updated successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update section', 'error')
    },
  })

  const deleteSectionMutation = useMutation({
    mutationFn: deleteClassSectionDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all })
      setSectionDeleteDialogOpen(false)
      setSectionToDelete(null)
      showToast('Section deleted successfully', 'success')
    },
    onError: (error: Error) => {
      showToast(
        error.message || 'Failed to delete section. It may have existing timetable records.',
        'error'
      )
    },
  })

  // Class handlers
  const handleAddClass = () => {
    setSelectedClass(null)
    setClassDialogOpen(true)
  }

  const handleEditClass = (classItem: Class) => {
    setSelectedClass(classItem)
    setClassDialogOpen(true)
  }

  const handleDeleteClass = (classItem: Class, hasTimetableRecords: boolean) => {
    setClassToDelete(classItem)
    setClassHasTimetableRecords(hasTimetableRecords)
    setClassDeleteDialogOpen(true)
  }

  const handleReactivateClass = (classItem: Class) => {
    reactivateClassMutation.mutate(classItem.id)
  }

  const handleClassSubmit = (data: { class_name: string; class_group: string; defaultSectionName?: string }) => {
    if (selectedClass) {
      updateClassMutation.mutate({ 
        id: selectedClass.id, 
        data: { class_name: data.class_name, class_group: data.class_group } 
      })
    } else {
      createClassMutation.mutate(data)
    }
  }

  const handleConfirmClassDelete = () => {
    if (classToDelete) {
      deleteClassMutation.mutate(classToDelete.id)
    }
  }

  // Section handlers
  const handleAddSection = (classId: string) => {
    setSelectedClassId(classId)
    setSelectedSection(null)
    setSectionDialogOpen(true)
  }

  const handleEditSection = (section: ClassSectionWithRelations) => {
    setSelectedSection(section)
    setSelectedClassId(section.class_id)
    setSectionDialogOpen(true)
  }

  const handleDeleteSection = (section: ClassSectionWithRelations) => {
    setSectionToDelete(section)
    setSectionDeleteDialogOpen(true)
  }

  const handleSectionSubmit = (data: { section: string; class_teacher_id: string | null }) => {
    if (selectedSection) {
      updateSectionMutation.mutate({
        id: selectedSection.id,
        data: {
          section: data.section,
          class_teacher_id: data.class_teacher_id || undefined,
        },
      })
    } else if (selectedClassId) {
      createSectionMutation.mutate({
        class_id: selectedClassId,
        section: data.section,
        class_teacher_id: data.class_teacher_id || undefined,
      })
    }
  }

  const handleConfirmSectionDelete = () => {
    if (sectionToDelete) {
      deleteSectionMutation.mutate(sectionToDelete.id)
    }
  }

  const getClassName = (classId: string) => {
    const classItem = classes.find((c) => c.id === classId)
    return classItem?.class_name || ''
  }

  const getSectionsForClass = (classId: string) => {
    return sections.filter((s) => s.class_id === classId)
  }

  if (classesLoading || sectionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (classesError || sectionsError) {
    return (
      <Alert severity="error">
        {(classesError as Error)?.message || (sectionsError as Error)?.message || 'Failed to load data'}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Search, Sort, and Add Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search classes by name or class group..."
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              size="small"
            />
          }
          label="Show inactive"
        />
        <SortMenu
          options={SORT_OPTIONS}
          value={sortConfig}
          onChange={setSortConfig}
        />
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setClassGroupsDialogOpen(true)}
        >
          Manage Groups
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClass}
        >
          Add Class
        </Button>
      </Box>

      <ClassList
        classes={classes}
        sections={sections}
        searchQuery={debouncedSearchQuery}
        sortConfig={sortConfig}
        schedules={schedules}
        showInactive={showInactive}
        onEditClass={handleEditClass}
        onDeleteClass={handleDeleteClass}
        onReactivateClass={handleReactivateClass}
        onEditSection={handleEditSection}
        onDeleteSection={handleDeleteSection}
        onAddSection={handleAddSection}
      />

      {/* Class Form Dialog */}
      <ClassFormDialog
        open={classDialogOpen}
        classItem={selectedClass}
        existingClasses={classes}
        classGroups={classGroups}
        onClose={() => {
          setClassDialogOpen(false)
          setSelectedClass(null)
        }}
        onSubmit={handleClassSubmit}
      />

      {/* Class Groups Dialog */}
      <ClassGroupsDialog
        open={classGroupsDialogOpen}
        schoolId={schoolId}
        groups={classGroups}
        onClose={() => setClassGroupsDialogOpen(false)}
        onCreate={(name) => createClassGroupMutation.mutateAsync(name)}
        onUpdate={(id, name) => updateClassGroupMutation.mutateAsync({ id, name })}
        onDelete={(id) => deleteClassGroupMutation.mutateAsync(id)}
      />

      {/* Class Delete/Deactivate Dialog */}
      <ConfirmDialog
        open={classDeleteDialogOpen}
        title={classHasTimetableRecords ? 'Deactivate Class' : 'Delete Class'}
        message={
          classToDelete
            ? classHasTimetableRecords
              ? `"${classToDelete.class_name}" has existing timetable records and will be deactivated instead of deleted. It will no longer appear in active lists but historical data will be preserved.`
              : `Are you sure you want to permanently delete "${classToDelete.class_name}"? This will also delete all its sections. This action cannot be undone.`
            : ''
        }
        onConfirm={handleConfirmClassDelete}
        onCancel={() => {
          setClassDeleteDialogOpen(false)
          setClassToDelete(null)
        }}
      />

      {/* Section Form Dialog */}
      <SectionFormDialog
        open={sectionDialogOpen}
        section={selectedSection}
        className={selectedClassId ? getClassName(selectedClassId) : ''}
        teachers={teachers}
        existingSections={selectedClassId ? getSectionsForClass(selectedClassId) : []}
        allSections={sections}
        onClose={() => {
          setSectionDialogOpen(false)
          setSelectedSection(null)
          setSelectedClassId(null)
        }}
        onSubmit={handleSectionSubmit}
      />

      {/* Section Delete Dialog */}
      <ConfirmDialog
        open={sectionDeleteDialogOpen}
        title="Delete Section"
        message={
          sectionToDelete
            ? `Are you sure you want to delete section "${getClassName(sectionToDelete.class_id)}-${sectionToDelete.section}"? This action cannot be undone.`
            : ''
        }
        onConfirm={handleConfirmSectionDelete}
        onCancel={() => {
          setSectionDeleteDialogOpen(false)
          setSectionToDelete(null)
        }}
      />
    </Box>
  )
}
